import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { range, smoothstep } from '@/lib/motion/math'
import { promise } from '@/constants/content'

import {
    enterProgress,
    isNear,
    measureSection,
    emptyMetrics,
    pinProgress,
    useEngine
} from '@/lib/motion/section'

import {
    Bracket,
    RevealWords,
    createStyleCache,
    createWordReveal
} from '@/components/landing/primitives'

const MARKER_RADIUS = 21
const MARKER_LENGTH = 2 * Math.PI * MARKER_RADIUS

const PromiseSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const frameRef = useRef<HTMLDivElement>(null)
    const ruleRef = useRef<HTMLDivElement>(null)
    const dividerRef = useRef<HTMLDivElement>(null)
    const statementRef = useRef<HTMLHeadingElement>(null)
    const labelRef = useRef<HTMLDivElement>(null)
    const markerRef = useRef<SVGCircleElement>(null)
    const footRef = useRef<HTMLDivElement>(null)

    useEngine(() => {
        const set = createStyleCache()
        const reveal = createWordReveal(statementRef.current)

        let metrics = emptyMetrics()

        return {
            measure: () => {
                if (sectionRef.current) metrics = measureSection(sectionRef.current, frameRef.current)
            },
            frame: (state) => {
                if (!isNear(metrics, state.scroll, state.vh)) return

                const enter = enterProgress(metrics, state.scroll, state.vh)
                const p = pinProgress(metrics, state.scroll)
                const words = range(0.03, 0.82, p)

                set(ruleRef.current, 'transform', `scaleX(${smoothstep(0.25, 0.95, enter).toFixed(4)})`)
                set(dividerRef.current, 'transform', `scaleY(${smoothstep(0.45, 1, enter).toFixed(4)})`)
                set(labelRef.current, 'opacity', smoothstep(0.55, 1, enter).toFixed(3))
                set(footRef.current, 'opacity', smoothstep(0.6, 1, enter).toFixed(3))
                set(markerRef.current, 'stroke-dashoffset', (MARKER_LENGTH * (1 - words)).toFixed(2))

                reveal(words)
            }
        }
    })

    return (
        <section id='promise' ref={sectionRef} aria-labelledby='promise-title' className='relative h-[220vh] md:h-[240vh]'>
            <div ref={frameRef} data-frame='' className='sticky top-0 h-svh overflow-hidden'>
                <div ref={ruleRef} aria-hidden='true' className='absolute inset-x-3 top-[84px] h-px origin-left bg-line md:inset-x-5' />
                <div ref={dividerRef} aria-hidden='true' className='absolute bottom-0 left-[58%] top-[84px] hidden w-px origin-top bg-line md:block' />

                <div className='relative flex h-full flex-col justify-end px-5 pb-24 md:justify-center md:px-10 md:pb-0'>
                    <div className='md:w-[54%]'>
                        <div ref={labelRef} className='flex items-center gap-4 text-haze'>
                            <Bracket>{promise.label}</Bracket>
                            <span aria-hidden='true' className='h-px w-12 bg-pale' />
                        </div>

                        <h2
                            id='promise-title'
                            ref={statementRef}
                            className='mt-6 text-[clamp(34px,5.1vw,92px)] font-light leading-[1.02] tracking-[-0.048em] md:mt-8'
                        >
                            <RevealWords text={promise.statement} />
                        </h2>
                    </div>
                </div>

                <div ref={footRef} className='absolute bottom-6 left-5 text-haze md:bottom-10 md:left-10'>
                    <Bracket>Early warning</Bracket>
                </div>

                <div aria-hidden='true' className='absolute bottom-5 right-5 size-12 md:bottom-9 md:right-10'>
                    <svg viewBox='0 0 48 48' className='absolute inset-0 size-full -rotate-90'>
                        <circle cx='24' cy='24' r={MARKER_RADIUS} fill='none' stroke='#dde3e5' strokeWidth='1' />
                        <circle
                            ref={markerRef}
                            cx='24'
                            cy='24'
                            r={MARKER_RADIUS}
                            fill='none'
                            stroke='#1d2a31'
                            strokeWidth='1'
                            strokeDasharray={MARKER_LENGTH}
                            strokeDashoffset={MARKER_LENGTH}
                        />
                    </svg>

                    <span className='absolute inset-0 flex items-center justify-center font-mono text-[11px] tabular-nums text-ink'>
                        01
                    </span>
                </div>
            </div>
        </section>
    )
}

export default PromiseSection
